/**
 * Branded identifiers.
 *
 * A question id and an option id are both strings, and mixing them up is the kind of bug that
 * survives code review and shows up as a wrong score. Branding makes the compiler reject it at
 * zero runtime cost — the brand is a phantom property that never exists at runtime.
 */

declare const brand: unique symbol;
type Brand<T, B extends string> = T & { readonly [brand]: B };

export type ExamId = Brand<string, 'ExamId'>;
export type QuestionId = Brand<string, 'QuestionId'>;
export type OptionId = Brand<string, 'OptionId'>;
export type ItemId = Brand<string, 'ItemId'>;
export type TargetId = Brand<string, 'TargetId'>;
export type TaxonomyId = Brand<string, 'TaxonomyId'>;
export type SessionId = Brand<string, 'SessionId'>;
export type MediaId = Brand<string, 'MediaId'>;

/** `sha256:<64 lowercase hex chars>`. */
export type ContentHash = Brand<string, 'ContentHash'>;
export type Seed = Brand<string, 'Seed'>;
export type IsoDateTime = Brand<string, 'IsoDateTime'>;
/** BCP-47 language tag, e.g. `en`, `pt-BR`, `de`. */
export type LanguageTag = Brand<string, 'LanguageTag'>;

/**
 * Casts a validated string into a branded type.
 *
 * Only call this where the value has actually been validated — normalizers and parsers. Calling it
 * to silence the compiler defeats the purpose of the brand.
 */
// The type parameter appears only in the return type by design: the caller picks the brand at the
// call site, as in `brandAs<ContentHash>(value)`. That is the whole point of the helper.
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- see the note above
export function brandAs<T extends string>(value: string): T {
  return value as T;
}

const CONTENT_HASH_PATTERN = /^sha256:[a-f0-9]{64}$/;

export function isContentHash(value: string): value is ContentHash {
  return CONTENT_HASH_PATTERN.test(value);
}
